<template>
  <div class="tm-page growth-site">
    <section class="tm-hero">
      <div class="tm-hero__copy">
        <p class="growth-eyebrow tm-eyebrow">
          <span
            class="growth-live-dot"
            aria-hidden="true"
          />
          {{ $t('trackmuse.eyebrow') }}
        </p>
        <p class="tm-audience">
          {{ $t('trackmuse.audience') }}
        </p>
        <h1>
          {{ $t('trackmuse.title') }}
          <span>{{ $t('trackmuse.accent') }}</span>
        </h1>
        <p class="tm-lead">
          {{ $t('trackmuse.subtitle') }}
        </p>
        <div class="tm-actions">
          <a
            class="growth-button growth-button--lime growth-button--large"
            data-test="trackmuse-cta"
            :href="trackMuseUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ $t('trackmuse.cta') }}
            <GrowthIcon name="up" />
          </a>
          <a
            class="growth-text-link"
            href="#workflow"
          >
            {{ $t('trackmuse.secondary') }}
            <GrowthIcon name="arrow" />
          </a>
        </div>
        <p class="tm-note">
          {{ $t('trackmuse.note') }}
        </p>
      </div>

      <div
        class="tm-board"
        :aria-label="$t('trackmuse.board.label')"
      >
        <div class="tm-board__top">
          <span>{{ $t('trackmuse.board.label') }}</span>
          <span>{{ $t('trackmuse.board.progress', { current: doneCount, total: stepKeys.length }) }}</span>
        </div>
        <div class="tm-board__columns">
          <section
            v-for="column in columns"
            :key="column.id"
            class="tm-column"
            :class="{ 'is-active': column.id === 'mix' }"
          >
            <h2>{{ $t(`trackmuse.board.columns.${column.id}`) }}</h2>
            <article
              v-if="column.id === 'mix'"
              class="tm-card tm-card--focus"
            >
              <p class="tm-kicker">
                {{ $t('trackmuse.board.session') }}
              </p>
              <h3>{{ $t('trackmuse.board.track') }}</h3>
              <p class="tm-meta">
                {{ $t('trackmuse.board.meta') }}
              </p>
              <ul>
                <li
                  v-for="(step, index) in stepKeys"
                  :key="step"
                >
                  <button
                    type="button"
                    :aria-pressed="steps[index]"
                    :data-test="`trackmuse-step-${step}`"
                    @click="toggleStep(index)"
                  >
                    <span
                      class="tm-check"
                      :class="{ 'is-done': steps[index] }"
                      aria-hidden="true"
                    />
                    <span :class="{ 'is-done': steps[index] }">
                      {{ $t(`trackmuse.board.steps.${step}`) }}
                    </span>
                  </button>
                </li>
              </ul>
              <div
                class="tm-progress"
                role="progressbar"
                :aria-valuenow="doneCount"
                aria-valuemin="0"
                :aria-valuemax="stepKeys.length"
                :aria-label="$t('trackmuse.board.progress', { current: doneCount, total: stepKeys.length })"
              >
                <span :style="{ width: `${progress}%` }" />
              </div>
              <p
                v-if="doneCount === stepKeys.length"
                class="tm-ready"
              >
                {{ $t('trackmuse.board.ready') }}
              </p>
            </article>
            <p
              v-else-if="!column.card"
              class="tm-empty"
            >
              {{ $t('trackmuse.board.empty') }}
            </p>
            <article
              v-else
              class="tm-card"
            >
              {{ column.card }}
            </article>
          </section>
        </div>
      </div>
    </section>

    <section
      id="workflow"
      class="tm-section"
    >
      <div class="tm-heading">
        <p class="growth-eyebrow">
          {{ $t('trackmuse.features.eyebrow') }}
        </p>
        <h2>{{ $t('trackmuse.features.title') }}</h2>
        <p>{{ $t('trackmuse.features.subtitle') }}</p>
      </div>
      <div class="tm-features">
        <article
          v-for="feature in features"
          :key="feature.id"
        >
          <span
            class="tm-icon"
            aria-hidden="true"
          >
            <GrowthIcon :name="feature.icon" />
          </span>
          <h3>{{ $t(`trackmuse.features.${feature.id}.title`) }}</h3>
          <p>{{ $t(`trackmuse.features.${feature.id}.description`) }}</p>
        </article>
      </div>
      <ul class="tm-extras">
        <li
          v-for="extra in extras"
          :key="extra"
        >
          <GrowthIcon name="check" />
          {{ $t(`trackmuse.extras.${extra}`) }}
        </li>
      </ul>
    </section>

    <section
      id="pricing"
      class="tm-section"
    >
      <div class="tm-heading">
        <p class="growth-eyebrow">
          {{ $t('trackmuse.pricing.eyebrow') }}
        </p>
        <h2>{{ $t('trackmuse.pricing.title') }}</h2>
      </div>
      <div class="tm-plans">
        <article class="tm-plan">
          <h3>{{ $t('trackmuse.pricing.free-name') }}</h3>
          <p class="tm-price">
            {{ $t('trackmuse.pricing.free-price') }}
            <span>{{ $t('trackmuse.pricing.free-period') }}</span>
          </p>
          <p class="tm-plan__note">
            {{ $t('trackmuse.pricing.free-note') }}
          </p>
          <ul>
            <li
              v-for="item in freeItems"
              :key="item"
            >
              {{ $t(`trackmuse.pricing.${item}`) }}
            </li>
          </ul>
          <a
            class="growth-button growth-button--dark"
            :href="trackMuseUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ $t('trackmuse.pricing.free-cta') }}
          </a>
        </article>
        <article class="tm-plan tm-plan--pro">
          <p class="refresh-pro-tag">
            {{ $t('trackmuse.pricing.pro-badge') }}
          </p>
          <h3>{{ $t('trackmuse.pricing.pro-name') }}</h3>
          <p class="tm-price">
            {{ $t('trackmuse.pricing.pro-price') }}
            <span>{{ $t('trackmuse.pricing.pro-period') }}</span>
          </p>
          <p class="tm-plan__note">
            {{ $t('trackmuse.pricing.pro-note') }}
          </p>
          <ul>
            <li
              v-for="item in proItems"
              :key="item"
            >
              {{ $t(`trackmuse.pricing.${item}`) }}
            </li>
          </ul>
          <a
            class="growth-button growth-button--lime"
            data-test="trackmuse-pro"
            :href="trackMuseUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ $t('trackmuse.pricing.pro-cta') }}
          </a>
        </article>
      </div>
    </section>

    <section class="tm-section tm-faq">
      <div class="tm-heading">
        <h2>{{ $t('trackmuse.faq.title') }}</h2>
      </div>
      <div class="tm-faq__list">
        <details
          v-for="item in faqItems"
          :key="item"
          data-test="trackmuse-faq"
        >
          <summary>{{ $t(`trackmuse.faq.${item}.q`) }}</summary>
          <p>{{ $t(`trackmuse.faq.${item}.a`) }}</p>
        </details>
      </div>
    </section>

    <section class="tm-close">
      <h2>{{ $t('trackmuse.final.title') }}</h2>
      <p>{{ $t('trackmuse.final.subtitle') }}</p>
      <a
        class="growth-button growth-button--lime growth-button--large"
        data-test="trackmuse-cta-final"
        :href="trackMuseUrl"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ $t('trackmuse.final.cta') }}
        <GrowthIcon name="up" />
      </a>
      <router-link
        class="tm-bridge"
        to="/distribute"
        data-test="trackmuse-indiefy"
      >
        {{ $t('trackmuse.final.bridge') }}
      </router-link>
    </section>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import GrowthIcon from '@/components/growth/GrowthIcon.vue';
import { usePageSeo } from '@/composables/usePageSeo';

const trackMuseUrl = 'https://trackmuse.app/?utm_source=indiefy&utm_medium=indiefy&utm_campaign=trackmuse-landing';

const { t } = useI18n();
const stepKeys = ['verse', 'bass', 'listen'];
const steps = ref([false, false, false]);
const doneCount = computed(() => steps.value.filter(Boolean).length);
const progress = computed(() => (doneCount.value / stepKeys.length) * 100);

const toggleStep = (index) => {
  steps.value[index] = !steps.value[index];
};

const columns = computed(() => [
  { id: 'idea', card: t('trackmuse.board.idea-card') },
  { id: 'progress', card: t('trackmuse.board.progress-card') },
  { id: 'mix' },
  { id: 'master', card: '' },
  { id: 'released', card: t('trackmuse.board.released-card') },
]);

const features = [
  { id: 'boards', icon: 'music' },
  { id: 'checklists', icon: 'check' },
  { id: 'ideas', icon: 'file' },
  { id: 'notes', icon: 'clock' },
  { id: 'timeline', icon: 'chart' },
  { id: 'analytics', icon: 'spark' },
];

const extras = ['templates', 'colors', 'languages', 'backup'];
const freeItems = ['free-1', 'free-2', 'free-3', 'free-4'];
const proItems = ['pro-1', 'pro-2', 'pro-3', 'pro-4'];
const faqItems = ['daw', 'free', 'pro'];

usePageSeo({
  titleKey: 'trackmuse.seo-title',
  descriptionKey: 'trackmuse.seo-description',
  jsonLd: computed(() => [{
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((id) => ({
      '@type': 'Question',
      name: t(`trackmuse.faq.${id}.q`),
      acceptedAnswer: {
        '@type': 'Answer',
        text: t(`trackmuse.faq.${id}.a`),
      },
    })),
  }]),
});
</script>

<style scoped>
.tm-page {
  --tm-paper: var(--growth-paper, #f6f7f2);
  --tm-card: var(--growth-card, #fff);
  --tm-ink: var(--growth-ink, #20221f);
  --tm-muted: var(--growth-muted, #62665d);
  --tm-line: var(--growth-line, #e0e3d9);
  --tm-lime: var(--growth-lime, #d5fc67);
  background: var(--tm-paper);
  color: var(--tm-ink);
}

.tm-hero,
.tm-section,
.tm-close {
  width: min(1120px, calc(100% - 40px));
  margin-inline: auto;
}

.tm-hero {
  padding: 56px 0 8px;
}

.tm-hero__copy {
  max-width: 720px;
}

.tm-eyebrow {
  display: flex;
  align-items: center;
  gap: 9px;
}

.tm-audience {
  margin-top: 18px;
  color: var(--tm-muted);
  font-size: 13px;
}

.tm-hero h1 {
  margin-top: 12px;
  font-size: clamp(44px, 5vw, 72px);
  font-weight: 550;
  letter-spacing: -0.06em;
  line-height: 0.98;
}

.tm-hero h1 span {
  display: block;
  color: #6f8157;
}

.tm-lead {
  max-width: 38em;
  margin-top: 22px;
  color: var(--tm-muted);
  font-size: 16px;
  line-height: 1.7;
}

.tm-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 18px 22px;
  margin-top: 28px;
}

.tm-note {
  margin-top: 16px;
  color: var(--tm-muted);
  font-size: 12px;
}

.tm-board {
  margin-top: 36px;
  background: #242720;
  color: #f4f6ee;
  border-radius: 20px;
  padding: 18px;
  box-shadow: 0 24px 60px rgba(32, 34, 31, 0.16);
  overflow: hidden;
}

.tm-board__top {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #c9d3b8;
}

.tm-board__columns {
  display: grid;
  grid-template-columns: 0.8fr 0.8fr 1.7fr 0.8fr 0.8fr;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 4px;
}

.tm-column {
  min-width: 0;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 12px;
}

.tm-column.is-active {
  background: rgba(213, 252, 103, 0.08);
  border-color: rgba(213, 252, 103, 0.45);
}

.tm-column h2 {
  margin-bottom: 10px;
  color: #d7decc;
  font-size: 11px;
  font-weight: 650;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.tm-card,
.tm-empty {
  background: #f7f8f3;
  color: #20221f;
  border-radius: 12px;
  padding: 12px;
  font-size: 13px;
  font-weight: 600;
}

.tm-empty {
  color: #7d8474;
  font-weight: 500;
  background: transparent;
  border: 1px dashed rgba(255, 255, 255, 0.16);
}

.tm-card--focus {
  font-weight: 500;
}

.tm-kicker,
.tm-meta,
.tm-ready {
  color: #62665d;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.tm-card--focus h3 {
  margin: 6px 0 4px;
  font-size: 22px;
  letter-spacing: -0.04em;
}

.tm-meta {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
}

.tm-card--focus ul {
  list-style: none;
  margin: 14px 0 0;
  padding: 0;
}

.tm-card--focus button {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  min-height: 44px;
  margin: 0;
  padding: 8px 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.tm-card--focus button span.is-done {
  color: #7b8470;
  text-decoration: line-through;
}

.tm-check {
  width: 18px;
  height: 18px;
  margin-top: 1px;
  flex: 0 0 auto;
  border: 1.5px solid #b7c0a8;
  border-radius: 6px;
  background: #fff;
}

.tm-check.is-done {
  border-color: #4d6a28;
  background: var(--tm-lime) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2320221f' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m5 12 4 4L19 6'/%3E%3C/svg%3E") center / 12px no-repeat;
}

.tm-progress {
  height: 6px;
  margin-top: 12px;
  border-radius: 99px;
  background: #e3e7da;
  overflow: hidden;
}

.tm-progress span {
  display: block;
  height: 100%;
  background: #71952e;
}

.tm-ready {
  margin-top: 10px;
  text-transform: none;
  letter-spacing: 0;
  color: #405727;
}

.tm-section {
  padding: 72px 0;
  scroll-margin-top: 96px;
}

.tm-heading {
  max-width: 640px;
  margin-bottom: 32px;
}

.tm-heading h2,
.tm-close h2 {
  margin-top: 12px;
  font-size: clamp(32px, 4vw, 48px);
  font-weight: 550;
  letter-spacing: -0.05em;
  line-height: 1.08;
}

.tm-heading p:last-child {
  margin-top: 14px;
  color: var(--tm-muted);
  font-size: 15px;
  line-height: 1.7;
}

.tm-features {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.tm-features article,
.tm-plan {
  background: var(--tm-card);
  border: 1px solid var(--tm-line);
  border-radius: 16px;
  padding: 22px;
}

.tm-icon {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  margin-bottom: 16px;
  border-radius: 12px;
  background: #e7eddc;
  color: #4f6434;
}

.tm-features article:nth-child(3n + 2) .tm-icon {
  background: #ece6f6;
  color: #6d5a8d;
}

.tm-features article:nth-child(3n) .tm-icon {
  background: #f3ecdf;
  color: #8a7345;
}

.tm-features h3,
.tm-plan h3 {
  font-size: 18px;
  letter-spacing: -0.03em;
}

.tm-features p {
  margin-top: 8px;
  color: var(--tm-muted);
  font-size: 14px;
  line-height: 1.65;
}

.tm-extras {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
  margin: 22px 0 0;
  padding: 0;
  list-style: none;
}

.tm-extras li {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  color: var(--tm-muted);
  font-size: 13px;
}

.tm-extras svg {
  width: 16px;
  height: 16px;
  color: #59732d;
}

.tm-plans {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: stretch;
}

.tm-plan {
  display: flex;
  flex-direction: column;
}

.tm-price {
  margin-top: 12px;
  font-size: 48px;
  font-weight: 550;
  letter-spacing: -0.05em;
  line-height: 1;
}

.tm-price span,
.tm-plan__note {
  color: var(--tm-muted);
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0;
}

.tm-plan__note {
  margin-top: 8px;
}

.tm-plan ul {
  margin: 20px 0 24px;
  padding: 0;
  list-style: none;
}

.tm-plan li {
  position: relative;
  padding: 8px 0 8px 18px;
  border-top: 1px solid var(--tm-line);
  font-size: 14px;
}

.tm-plan li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 16px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--tm-lime);
}

.tm-plan .growth-button {
  margin-top: auto;
  width: 100%;
}

.tm-plan--pro {
  background: #242720;
  border-color: #242720;
  color: #f4f6ee;
}

.tm-plan--pro .refresh-pro-tag {
  color: var(--tm-lime);
  margin-bottom: 14px;
}

.tm-plan--pro .tm-price span,
.tm-plan--pro .tm-plan__note {
  color: #c8d1bb;
}

.tm-plan--pro li {
  border-top-color: rgba(255, 255, 255, 0.12);
}

.tm-faq__list {
  display: grid;
  gap: 10px;
}

.tm-faq details {
  background: var(--tm-card);
  border: 1px solid var(--tm-line);
  border-radius: 14px;
  padding: 6px 18px;
}

.tm-faq summary {
  min-height: 52px;
  display: flex;
  align-items: center;
  cursor: pointer;
  font-weight: 600;
  letter-spacing: -0.02em;
}

.tm-faq p {
  margin: 0 0 16px;
  color: var(--tm-muted);
  font-size: 14px;
  line-height: 1.7;
}

.tm-close {
  margin-bottom: 72px;
  padding: 48px 32px;
  border-radius: 24px;
  background: #242720;
  color: #f4f6ee;
  text-align: center;
}

.tm-close p {
  max-width: 36em;
  margin: 14px auto 24px;
  color: #c8d1bb;
  font-size: 15px;
  line-height: 1.7;
}

.tm-bridge {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  margin-top: 14px;
  color: #d5fc67;
  font-size: 13px;
  font-weight: 600;
}

.tm-bridge:hover {
  color: #fff;
}

:global(.body--dark) .tm-page {
  --tm-paper: #1d201b;
  --tm-card: #262a23;
  --tm-ink: #f0f3e9;
  --tm-muted: #aab39e;
  --tm-line: #3b4134;
}

:global(.body--dark) .tm-hero h1 span {
  color: #d5fc67;
}

@media (max-width: 980px) {
  .tm-features {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .tm-plans {
    grid-template-columns: 1fr;
  }

  .tm-board {
    overflow-x: auto;
  }

  .tm-board__columns {
    grid-template-columns: 148px 148px minmax(240px, 1.5fr) 148px 148px;
    width: max-content;
    min-width: 100%;
  }

  .tm-column {
    min-width: 148px;
  }
}

@media (max-width: 640px) {
  .tm-hero,
  .tm-section,
  .tm-close {
    width: min(100% - 32px, 1120px);
  }

  .tm-hero {
    padding-top: 32px;
  }

  .tm-actions .growth-button,
  .tm-close .growth-button {
    width: 100%;
  }

  .tm-features {
    grid-template-columns: 1fr;
  }


  .tm-section {
    padding: 52px 0;
  }
}
</style>
